import { Plus, Trash } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { StringListFields } from '@/components/ui/StringListFields';
import {
  claveCombinacion,
  cleanStringList,
  emptyOrderVariant,
  parseIntegerField,
  type OrderVariantForm,
} from './products-payload';

/**
 * Editor de `datosParaOrden`: las variantes que el bot debe preguntar antes de armar la orden.
 *
 * Cada variante es un nombre (ej. "Color") y su lista de opciones (ej. "Negro", "Blanco"). El ORDEN
 * importa: es el orden en el que el bot las pide en la conversación, así que se agregan al final y no se
 * reordenan solas. Un producto sin variantes se vende solo con la cantidad.
 *
 * **Cantidad al lado de cada opción** (`inventario`, 21-sep-2026): cada opción de CADA variante lleva su
 * campo de unidades en la misma fila, porque cada valor tiene su propio stock (talla 40 → 5, talla 41 →
 * 2, dama 36 → 10…). Eso es el inventario **por variante suelta**.
 *
 * El stock que depende del CRUCE de dos ejes (talla 42 · negro) no se puede colgar de una opción suelta:
 * eso vive en `VariantInventoryFields` (la matriz), y `ProductDialog` solo la muestra cuando el producto
 * ya tiene stock por combinación o cuando el negocio lo pide.
 *
 * OJO con el ancho: `Input` del sistema de diseño es de ancho completo (`w-full`), así que el campo de
 * cantidad va envuelto en un contenedor de ancho fijo (`w-24 shrink-0`) para que la opción de la
 * izquierda no se colapse (el mismo patrón que `VariantInventoryFields`).
 */
export interface OrderVariantFieldsProps {
  rows: OrderVariantForm[];
  onChange: (rows: OrderVariantForm[]) => void;
  disabled?: boolean;
  className?: string;
  /**
   * Inventario EN LA MISMA FILA de cada opción: mapa `clave canónica → cantidad en texto` (el mismo que
   * usa `VariantInventoryFields`). Se omite solo si el producto no tiene variantes con opciones.
   */
  inventario?: {
    values: Record<string, string>;
    onChange: (values: Record<string, string>) => void;
  };
}

export function OrderVariantFields({
  rows,
  onChange,
  disabled = false,
  className,
  inventario,
}: OrderVariantFieldsProps) {
  function updateRow(id: string, patch: Partial<OrderVariantForm>) {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function removeRow(id: string) {
    onChange(rows.filter((row) => row.id !== id));
  }

  function updateOpcion(id: string, indice: number, valor: string) {
    onChange(
      rows.map((row) =>
        row.id === id
          ? { ...row, opciones: row.opciones.map((o, position) => (position === indice ? valor : o)) }
          : row,
      ),
    );
  }

  function removeOpcion(id: string, indice: number) {
    onChange(
      rows.map((row) =>
        row.id === id
          ? { ...row, opciones: row.opciones.filter((_o, position) => position !== indice) }
          : row,
      ),
    );
  }

  /** Cantidad de una opción: la clave canónica es la de la combinación de ESE eje (como en la matriz). */
  function cantidadDe(nombreVariante: string, opcion: string): string {
    return inventario?.values[claveCombinacion({ [nombreVariante]: opcion })] ?? '';
  }

  function setCantidad(nombreVariante: string, opcion: string, cantidad: string) {
    if (!inventario) return;
    inventario.onChange({
      ...inventario.values,
      [claveCombinacion({ [nombreVariante]: opcion })]: cantidad,
    });
  }

  /** «2 de 5 opciones con cantidad · 7 unidades en total» (o el aviso de que ninguna la tiene). */
  function resumenCantidades(row: OrderVariantForm): string {
    const nombre = row.nombre.trim();
    const opciones = cleanStringList(row.opciones);
    const conCantidad = opciones.filter(
      (opcion) => cantidadDe(nombre, opcion).trim().length > 0,
    );
    const total = conCantidad.reduce(
      (suma, opcion) => suma + (parseIntegerField(cantidadDe(nombre, opcion)) ?? 0),
      0,
    );

    return conCantidad.length > 0
      ? `${conCantidad.length} de ${opciones.length} opciones con cantidad · ${total} unidades en total`
      : 'Ninguna opción tiene cantidad: este producto se venderá sin control por variante.';
  }

  return (
    <div className={className}>
      {rows.length > 0 ? (
        <div className="space-y-3">
          {rows.map((row, index) => {
            const nombre = row.nombre.trim();
            // La cantidad solo se puede colgar de una opción si la variante tiene nombre y opciones.
            const conCantidad =
              Boolean(inventario) && nombre.length > 0 && cleanStringList(row.opciones).length > 0;

            return (
              <div
                key={row.id}
                className="rounded-panel border border-line bg-surface-muted/40 p-3"
              >
                <div className="flex items-center gap-3">
                  <Input
                    value={row.nombre}
                    placeholder="ej. Color"
                    disabled={disabled}
                    aria-label={`Nombre de la variante ${index + 1}`}
                    onChange={(event) => updateRow(row.id, { nombre: event.target.value })}
                  />

                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={disabled}
                    aria-label={`Eliminar la variante ${index + 1}`}
                    onClick={() => removeRow(row.id)}
                  >
                    <Trash size={16} />
                  </Button>
                </div>

                {conCantidad ? (
                  <div className="mt-3">
                    <div className="flex items-center gap-3">
                      <span className="flex-1 text-caption font-medium text-ink-soft">Opciones</span>
                      <span className="w-24 shrink-0 text-center text-caption font-medium text-ink-soft">
                        Unidades
                      </span>
                      <span className="size-10 shrink-0" aria-hidden="true" />
                    </div>

                    <div className="mt-1 space-y-2">
                      {row.opciones.map((opcion, posicion) => (
                        <div key={posicion} className="flex items-center gap-3">
                          <Input
                            value={opcion}
                            placeholder="ej. Negro"
                            disabled={disabled}
                            aria-label={`Opción ${posicion + 1} de ${nombre}`}
                            onChange={(event) => updateOpcion(row.id, posicion, event.target.value)}
                          />

                          <span className="w-24 shrink-0">
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              inputMode="numeric"
                              placeholder="Sin control"
                              disabled={disabled}
                              aria-label={`Cantidad de ${opcion.trim() || `opción ${posicion + 1}`}`}
                              value={cantidadDe(nombre, opcion.trim())}
                              onChange={(event) =>
                                setCantidad(nombre, opcion.trim(), event.target.value)
                              }
                            />
                          </span>

                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={disabled}
                            aria-label={`Eliminar la opción ${posicion + 1} de ${nombre}`}
                            onClick={() => removeOpcion(row.id, posicion)}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2"
                      disabled={disabled}
                      leftIcon={<Plus size={14} />}
                      onClick={() => updateRow(row.id, { opciones: [...row.opciones, ''] })}
                    >
                      Agregar opción
                    </Button>

                    <p className="mt-2 text-caption text-ink-muted">{resumenCantidades(row)}</p>
                  </div>
                ) : (
                  <StringListFields
                    className="mt-3"
                    values={row.opciones}
                    onChange={(opciones) => updateRow(row.id, { opciones })}
                    itemLabel="Opción"
                    placeholder="ej. Negro"
                    addLabel="Agregar opción"
                    disabled={disabled}
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      <Button
        variant="ghost"
        size="sm"
        className={rows.length > 0 ? 'mt-2' : undefined}
        disabled={disabled}
        leftIcon={<Plus size={14} />}
        onClick={() => onChange([...rows, emptyOrderVariant()])}
      >
        Agregar variante
      </Button>
    </div>
  );
}
